import { useTranslate } from '@/shared/lib'
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, type Ref, watch } from 'vue'
import { useModel } from './model.ts'
import { useRoute, useRouter } from 'vue-router'
import { useAddInventoryObject } from '@/features/Inventory/ObjectAdd'
import { useRemoveInventObject } from '@/features/Inventory/ObjectRemove'
import { useRelocateInventObject } from '@/features/Inventory/ObjectRelocate'
import { useCreateInventObject } from '@/features/Inventory/ObjectCreate'
import { isPoint, resolveFigureGeomType } from '@/features/Inventory/ObjectCreate/model/helpers.ts'
import { navigateToCreateV2FromMap } from './navigateToCreateV2FromMap.ts'
import { parseLngLatFromQuery } from '@/features/Inventory/CreatePassportV2Batch/model/mapEntryQuery.ts'
import { useMapSelect } from '@/shared/ui/VMapSelect'
import { useMapAreaSelect } from '@/shared/ui/VMapAreaSelect'
import { InventObject } from './classes/InventObject.ts'
import { InventObjectCreator } from './classes/InventObjectCreator.ts'
import { debounce } from 'lodash'
import { booleanContains, buffer, nearestPointOnLine, point, polygonToLine } from '@turf/turf'
import { useInventObjectCreateModal, useMapMoveStore } from '@/widgets'
import { ObjectTypeFunctionalType } from '@/entities/Manuals/ObjectTypes'
import { usePlaceInventObjectOnMap } from '@/features/Inventory/PlaceOnMap'
import { useGlobalSearch } from '@/widgets/GlobalSearch'
import type { MapComponentExpose } from './adapters/types.ts'
import type { InventoryMapCluster, InventoryMapGeometry } from './types.ts'
import { focusLocationFromCoordinates } from './helpers.ts'
import { useAuth } from '@/entities/Auth'
import { useNotification } from '@/shared/ui'

type SelectCoordsType = '' | 'square' | 'circle'
type MapType = 'scheme' | 'satellite' | 'hybrid'

const MAP_LAYER_READY_TIMEOUT_MS = 5000

/**
 * Содержит бизнес-логику inventory-карты, не завязанную на конкретный map-provider.
 * UI-компонент передаёт сюда ref карты и получает state/handlers для provider-компонентов.
 *
 * @param map — ref provider-компонента с expose entity
 * @returns состояние и обработчики inventory-карты
 */
export const useInventoryMapController = (map: Ref<MapComponentExpose | undefined>) => {
  const route = useRoute()
  const router = useRouter()
  const moveStore = useMapMoveStore()
  const auth = useAuth()
  const { addWarning, addSuccess } = useNotification()
  const { trs } = useTranslate()

  const mapObjectId = computed(() => Number(route.params.object_id))

  const skipInitialBoundsFit = computed(() => parseLngLatFromQuery(route.query) != null)

  const full = ref(false)
  const mapType = ref<MapType>('scheme')
  const isLayerSwitching = ref(false)

  /* Принудительное использование альтернативной (Leaflet/OSM) карты, выбор пользователя сохраняется */
  const useAlternativeMap = ref(localStorage.getItem('invent_map_alternative') === 'true')
  watch(useAlternativeMap, (value) => {
    localStorage.setItem('invent_map_alternative', String(value))
  })

  const pointerScreenCoordinates = ref([0, 0])
  const pointerMapCoordinates = ref([0, 0])

  const {
    center,
    zoom,
    bounds,
    isProject,
    action,
    addObjectType,
    inventObjectTypes,
    typesDict,
    objectsList,
    mapClusters,
    data: objectListData,
    selectedObjects,
    expandedClusterIds,
    selectObject,
    selectAddObject,
    isSelected,
    selectedClear,
    selectedHandler: baseSelectedHandler,
    filter,
    getNearestRootObjectId,
    editSelectObject,
    editNewPoint,
    isEditNewPoint,
    setIsEditNewPoint,
    loadCenterMap,
    loadDataFiltered,
    debouncedReload,
    preSelectedObjectIds,
    isMapDataLoading,
  } = useModel(map, mapObjectId, { skipInitialBoundsFit })

  const isMapBusy = computed(() => isMapDataLoading.value || isLayerSwitching.value)

  const waitForMapEntity = (): Promise<void> =>
    new Promise((resolve) => {
      if (map.value?.entity) {
        resolve()
        return
      }

      const stop = watch(
        map,
        (instance) => {
          if (instance?.entity) {
            stop()
            window.clearTimeout(timeoutId)
            resolve()
          }
        },
        { immediate: true },
      )

      const timeoutId = window.setTimeout(() => {
        stop()
        resolve()
      }, MAP_LAYER_READY_TIMEOUT_MS)
    })

  const notifyMapTypeChanged = (type: MapType) => {
    addSuccess(trs('itf.map_type_changed', { type: trs(`itf.map_types.${type}`) }))
  }

  const debouncedBoundsSync = debounce(() => {
    if (!map.value?.entity) return
    if (map.value.entity.bounds) {
      bounds.value = map.value.entity.bounds
    }
  }, 300)

  /** Sync zoom immediately from listener (same as WCoordsMap); do not use `entity.zoom` — it can lag or be wrong. */
  const updateHandler = (event: any) => {
    const nextCenter = event?.location?.center
    const z = event?.location?.zoom

    if (Array.isArray(nextCenter) && nextCenter.length === 2) {
      center.value = nextCenter
    }

    if (typeof z === 'number' && Number.isFinite(z)) {
      zoom.value = z
    }

    debouncedBoundsSync()
  }

  watch([zoom, bounds], () => debouncedReload(mapObjectId))
  watch(map, () => debouncedReload(mapObjectId))

  watch(useAlternativeMap, async (useAlternative) => {
    isLayerSwitching.value = true
    await nextTick()
    await waitForMapEntity()
    isLayerSwitching.value = false

    addSuccess(
      trs(useAlternative ? 'itf.map_layer_switched_alternative' : 'itf.map_layer_switched_yandex'),
    )
    void loadDataFiltered(mapObjectId).catch(() => {})
  })

  watch(mapType, async (nextType, prevType) => {
    isLayerSwitching.value = true
    await nextTick()
    await waitForMapEntity()
    isLayerSwitching.value = false

    if (prevType != null && nextType !== prevType) {
      notifyMapTypeChanged(nextType)
    }
  })

  /** Пороги zoom для размера маркера (small/dot) — визуальные, к кластеризации не относятся. */
  const zoomSwitchValue = 15.5
  const zoomSmallMarker = 13

  const updateCoordsHandler = () => {
    debouncedReload(mapObjectId)
  }

  /**
   * Функция отслеживания перемещения мыши по карте
   * @param _
   * @param event
   */
  const mouseMoveHandler = (_: any, event: any) => {
    pointerScreenCoordinates.value = event.screenCoordinates
    pointerMapCoordinates.value = event.coordinates

    objectMouseMoveHandler(event)
  }

  let suppressNextMapClickClear = false

  const { save: saveObject } = useAddInventoryObject()

  /**
   * Функция обработки нажатия кнопки мыши
   * @param object
   * @param event
   */
  const clickHandler = (object: any, event: any) => {
    if (isEditFigure.value || isPlaceMode.value) return

    // Object click is followed by provider-level map click. Ignore exactly one trailing map click,
    // otherwise selectedObjects is cleared before InventoryMapInfo can render.
    if (suppressNextMapClickClear) {
      suppressNextMapClickClear = false
      return
    }

    if (object?.type !== 'marker' && object?.type !== 'feature') selectedClear()
  }

  const selectedHandler = (coords: number[][]) => {
    suppressNextMapClickClear = true
    baseSelectedHandler(coords)
  }

  /* Функция добавления объекта */
  const { show } = useInventObjectCreateModal()

  const addObject = async (
    coordinates: any[],
    missFunctionalType: boolean = false,
    options: { [key: string]: any } = {},
  ) => {
    if (!addObjectType.value) return

    const functionalType = addObjectType.value.functional_type
    const figureType = resolveFigureGeomType(addObjectType.value.figure)

    /** Map draw finish → Create V2 (point, line, polygon). */
    if (!missFunctionalType) {
      try {
        const parentLookup = isPoint(figureType) ? coordinates : coordinates[0]
        const parentId = await getNearestRootObjectId(
          Array.isArray(parentLookup?.[0]) ? coordinates : [parentLookup],
        )
        const ok = navigateToCreateV2FromMap(router, route, {
          inventTypeId: addObjectType.value.id,
          parentId,
          coordinates,
          zoom: zoom.value,
        })
        if (ok) {
          action.value = 'select'
          addObjectType.value = null
        }
      } catch {
        // getNearestRootObjectId shows toast and throws when no bind target
      }
      return
    }

    try {
      let result: any = {
        coordinates: coordinates,
        group_map_ids: [],
      }

      if (functionalType !== ObjectTypeFunctionalType.STANDARD) {
        result = await show(coordinates)
      }

      saveObject({
        name: generateName(),
        parent_id: await getNearestRootObjectId(coordinates),
        object_inventory_type_id: addObjectType.value.id,
        is_projecting: isProject.value,
        ...result,
        ...options,
      }).then(() => {
        loadDataFiltered(mapObjectId)
      })
    } catch (e) {}
  }

  /** Restore map center/zoom after returning from Create V2 (`?lng=&lat=&zoom=`). */
  function applyMapLocationFromQuery() {
    const loc = parseLngLatFromQuery(route.query)
    if (!loc) return
    center.value = [loc.lng, loc.lat]
    if (loc.zoom != null) {
      zoom.value = loc.zoom
    }
    if (map.value?.entity) {
      map.value.entity.setLocation({
        center: [loc.lng, loc.lat],
        zoom: loc.zoom ?? map.value.entity.zoom,
      })
    }
  }

  watch(
    () => [map.value, route.query.lng, route.query.lat, route.query.zoom],
    () => applyMapLocationFromQuery(),
    { immediate: true },
  )

  /* Методы и свойства создания объектов */
  const {
    isEditFigure,
    drawHandler,
    drawDownHandler,
    editFeature,
    pointerCount,
    initDrawHandler,
    cancelPointHandler,
    isQuickMapClick,
  } = useCreateInventObject(pointerMapCoordinates, addObjectType, addObject)

  /* Генерация имени - первичный вариант */
  const generateName = () => {
    if (addObjectType.value) {
      const count = objectsList.value.filter(
        (item) => item.getObjectSubTypeId() === addObjectType.value?.id,
      ).length

      return `${addObjectType.value.name} ${count + 1}`
    }

    return ''
  }

  /**
   * Разводит точки, совпадающие по координате, по окружности (px). Кластеризация
   * теперь полностью серверная (`POST /objects/map`) — это единственная client-side
   * геометрия, что осталась: покрывает и случайное совпадение координат, и
   * «разведённую» стопку (`colocated`), чьи участники после клика вернулись
   * сервером как обычные объекты с одинаковой точкой.
   * @param items
   * @param radius
   */
  function buildCoincidentOffsetMap(
    items: InventObject[],
    radius = 28,
  ): Map<number, [number, number]> {
    const coordKey = (m: InventObject) => (m.getMapCoords() as number[]).join(',')
    const byCoord = new Map<string, InventObject[]>()

    for (const m of items) {
      const key = coordKey(m)
      if (!byCoord.has(key)) byCoord.set(key, [])
      byCoord.get(key)!.push(m)
    }

    const offsetMap = new Map<number, [number, number]>()

    for (const group of byCoord.values()) {
      if (group.length === 1) {
        offsetMap.set(group[0].getId(), [0, 0])
        continue
      }

      // Первый маркер остаётся в центре, остальные расходятся по окружности
      offsetMap.set(group[0].getId(), [0, 0])
      const rest = group.slice(1)
      const angleStep = (2 * Math.PI) / rest.length
      rest.forEach((item, idx) => {
        const angle = angleStep * idx - Math.PI / 2
        offsetMap.set(item.getId(), [
          Math.round(Math.cos(angle) * radius),
          Math.round(Math.sin(angle) * radius),
        ])
      })
    }

    return offsetMap
  }

  /**
   * Точки/фигуры и кластеры вьюпорта — как отдал сервер, без клиентской агрегации.
   * `clusters`/`stacks` рендерятся провайдером как единый маркер-бабл;
   * `stacks` (`colocated`) визуально отличаются (кольцо вместо числа при раскрытии).
   */
  const objects = computed(() => {
    const features: InventObject[] = []
    const points: InventObject[] = []
    const selectedPoints: InventObject[] = []

    objectsList.value.forEach((item) => {
      const obj = item as InventObject
      if (!obj.isPoint()) {
        features.push(obj)
        return
      }
      if (selectedObjects.value.includes(obj.getId())) {
        selectedPoints.push(obj)
      } else {
        points.push(obj)
      }
    })

    const offsetRadius = zoom.value < zoomSmallMarker ? 6 : zoom.value <= zoomSwitchValue ? 17 : 28
    const offsetMap = buildCoincidentOffsetMap([...points, ...selectedPoints], offsetRadius)

    const markers = points.map((item) => ({
      item,
      pixelOffset: offsetMap.get(item.getId()) ?? ([0, 0] as [number, number]),
    }))
    const selectedMarkers = selectedPoints.map((item) => ({
      item,
      pixelOffset: offsetMap.get(item.getId()) ?? ([0, 0] as [number, number]),
    }))

    const clusters: InventoryMapCluster[] = []
    const stacks: InventoryMapCluster[] = []
    const spiderMembers: { item: InventObject; origin: [number, number] }[] = []
    const spiderOrigins: { clusterId: string; coordinates: [number, number] }[] = []

    mapClusters.value.forEach((cluster) => {
      if (!cluster.colocated) {
        clusters.push(cluster)
        return
      }

      /** Раскрытая (`expand`) стопка — сервер уже посчитал кольцо, рендерим его участников + ножку к центру. */
      if (cluster.members?.length) {
        spiderOrigins.push({ clusterId: cluster.id, coordinates: cluster.coordinates })

        cluster.members.forEach((member) => {
          if (!auth.hasElementPermission(member.type_id, 'read')) return

          const type = typesDict.value[String(member.type_id)]
          if (!type) return

          try {
            const item = InventObjectCreator.create(
              {
                id: member.id,
                type_id: member.type_id,
                name: member.name,
                geometry: { type: 'Point', coordinates: member.coordinates },
              },
              type,
            )
            spiderMembers.push({ item, origin: member.origin })
          } catch (e) {
            console.warn(`Skipping stack member ${member.id}: ${e}`)
          }
        })
        return
      }

      stacks.push(cluster)
    })

    return { features, markers, selectedMarkers, clusters, stacks, spiderMembers, spiderOrigins }
  })

  /** Реактивный сигнал синхронизации координат с формой ручного ввода.
   *  Форма конкретного объекта реагирует только если objectId совпадает. */
  const coordsSync = ref<{ objectId: number; coords: any[] } | null>(null)
  provide('coords-sync', coordsSync)

  /** Сигнал обратной связи: форма ручного ввода координат → карта. */
  const coordsFormUpdate = ref<{ objectId: number; coords: any[] } | null>(null)
  provide('coords-form-update', coordsFormUpdate)

  const normalizeFormCoords = (coords: any[]) =>
    coords
      .map((coord: any) => [Number(coord?.[0]), Number(coord?.[1])] as [number, number])
      .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))

  const closePolygonRing = (coords: [number, number][]) => {
    if (!coords.length) return coords

    const first = coords[0]
    const last = coords[coords.length - 1]

    if (first[0] === last[0] && first[1] === last[1]) {
      return coords
    }

    return [...coords, [...first] as [number, number]]
  }

  const getMapCoordinatesFromForm = (object: InventObject, coords: [number, number][]) => {
    if (object.isPoint()) {
      return coords.length ? coords[0] : null
    }

    if (object.isPolygon()) {
      return coords.length >= 3 ? [closePolygonRing(coords)] : null
    }

    return coords.length >= 2 ? coords : null
  }

  const applyObjectCoordinates = (object: InventObject, coordinates: any) => {
    const nextCoordinates = structuredClone(coordinates)

    object.getFeature().geometry = {
      ...object.getFeature().geometry,
      coordinates: nextCoordinates,
    }
    object.coordinates = object.transformCoords(nextCoordinates)
  }

  watch(coordsFormUpdate, (val) => {
    if (!val) return

    const targetObject = (objectsList.value as InventObject[]).find(
      (object) => object.getId() === val.objectId,
    )
    if (!targetObject) return

    const coordinates = getMapCoordinatesFromForm(targetObject, normalizeFormCoords(val.coords))
    if (!coordinates) return

    applyObjectCoordinates(targetObject, coordinates)

    const editObject = editSelectObject.value as InventObject | null
    if (editObject?.getId() === val.objectId && editObject !== targetObject) {
      applyObjectCoordinates(editObject, coordinates)
    }

    objectsList.value = [...(objectsList.value as InventObject[])]
  })

  /** Подгрузка функции удаления объекта **/
  const { remove } = useRemoveInventObject(objectListData, selectedObjects)

  /**
   * Функция обработки клика по объекту
   * @param object
   * @param event
   */
  const objectClickHandler = (object: InventObject, event: MouseEvent) => {
    if (isPlaceMode.value) return

    suppressNextMapClickClear = true

    /* Удаляем объект по клику */
    if (action.value === 'remove') {
      remove([object.getId()])
    }

    /* Добавляем объект в выборку */
    if (action.value === 'select') {
      if (event.shiftKey) {
        selectAddObject(object)
      } else {
        selectObject(object)
      }
    }

    /* Функция редактирования объекта */
    if (action.value === 'move') {
      if (object.isPoint()) return

      if (!canRelocate(object)) {
        addWarning('Нет прав на перемещение объекта этого типа')
        return
      }

      editSelectObject.value = object
    }

    /* Выбираем все элементы при клике с зажатым shift */
    if (event.shiftKey && action.value === 'select') {
      const cord: any = object.getMapCoords()
      const radiusInMeters = 5

      const centerPoint = point(cord)
      const bufferCircle: any = buffer(centerPoint, radiusInMeters, { units: 'meters' })

      objectsList.value.forEach((item) => {
        const itemCoords: any = item.getMapCoords()
        const itemPoint = point(itemCoords)

        if (booleanContains(bufferCircle, itemPoint)) {
          selectAddObject(item as InventObject)
        }
      })
    }
  }

  /** Функция обработки перетаскивания элемента */
  const { relocate } = useRelocateInventObject()

  const { placePreviewType, isPlaceMode, handlePlaceMapClick, exitPlaceMode } =
    usePlaceInventObjectOnMap({
      route,
      router,
      mapGroupId: mapObjectId,
      inventObjectTypes,
      selectedObjects,
      relocate,
      onPlaced: () => loadDataFiltered(mapObjectId),
    })

  /** Place-mode cursor preview: square point marker in element type color (same as Add for Point). */
  const placePointerType = computed(() => {
    const src = placePreviewType.value
    if (!src) return null
    return {
      ...src,
      figure: {
        ...src.figure,
        geom_type: 'Point',
        geom_subtype: 'square',
      },
    }
  })

  /**
   * Проверяет, может ли пользователь перемещать объект данного типа.
   * Опирается на element-права роли: если для типа права не заданы — доступ
   * по умолчанию разрешён.
   *
   * @param object - Объект карты
   * @returns true, если перемещение (relocate) разрешено
   */
  const canRelocate = (object: InventObject): boolean => {
    return auth.hasElementPermission(object.getObjectTypeId(), 'relocate')
  }

  /**
   * Показывает предупреждение, когда пользователь пытается взять метку (точку),
   * перемещение которой запрещено. Для меток drag заблокирован на уровне draggable,
   * поэтому обратную связь даём по mousedown.
   *
   * @param object - Объект карты, по которому нажали
   */
  const objectMoveBlockedHandler = (object: InventObject) => {
    if (canRelocate(object)) return
    addWarning(trs('itf.not_permission_for_relacate'))
  }

  /**
   * Фиксирует исходные координаты объекта перед началом перетаскивания.
   * Если объект уже зарегистрирован в очереди ожидания — originalCoords не перезаписываются,
   * чтобы при повторных перемещениях откат происходил к самому первому положению.
   *
   * @param object - Перемещаемый объект карты
   */
  const objectStartDragHandler = (object: InventObject) => {
    if (!moveStore.has(object.getId())) {
      moveStore.register(
        object.getId(),
        object.getName(),
        JSON.parse(JSON.stringify(object.getMapCoords())),
      )
    }
  }

  /**
   * Регистрирует функции подтверждения и отката после завершения перетаскивания.
   * Не блокирует дальнейшее взаимодействие с картой — панель подтверждения
   * отображается отдельно и не требует немедленного ответа пользователя.
   *
   * @param object - Перемещённый объект карты
   * @param coordinates - Новые координаты после перетаскивания
   */
  const objectEndDragHandler = (object: InventObject, coordinates: any) => {
    if (!canRelocate(object)) return

    const originalCoords = moveStore.entries.get(object.getId())?.originalCoords

    moveStore.updateActions(
      object.getId(),
      () => {
        relocate(object.getId(), {
          type: object.isPoint() ? 'Point' : object.isPolygon() ? 'Polygon' : 'LineString',
          coordinates,
        } as InventoryMapGeometry)
      },
      () => {
        if (!originalCoords) return

        object.getFeature().geometry = {
          ...object.getFeature().geometry,
          coordinates: originalCoords,
        }

        if (object.isPoint()) {
          coordsSync.value = { objectId: object.getId(), coords: [originalCoords] }
        } else if (object.isPolygon()) {
          coordsSync.value = { objectId: object.getId(), coords: originalCoords[0] }
        } else {
          coordsSync.value = { objectId: object.getId(), coords: originalCoords }
        }
      },
    )
  }

  /**
   * Функция обработки перемещения точек трансформации объекта вместе с объектом
   * @param object
   * @param coordinates
   */
  const objectMoveDragHandler = (object: InventObject, coordinates: any) => {
    if (editSelectObject.value) {
      editSelectObject.value.getFeature().geometry.coordinates = coordinates
    }

    if (object.isPoint()) {
      coordsSync.value = { objectId: object.getId(), coords: [coordinates] }
    } else if (object.isPolygon()) {
      coordsSync.value = { objectId: object.getId(), coords: coordinates[0] }
    } else {
      coordsSync.value = { objectId: object.getId(), coords: coordinates }
    }
  }

  /* Определяем стиль курсора в зависимости от типа действия */
  const cursorStyleFromObject = computed(() => {
    if (isPlaceMode.value) return { cursor: 'crosshair' }
    switch (action.value) {
      case 'select':
      case 'remove':
        return { cursor: 'pointer' }
      case 'move':
        return { cursor: 'move' }
      default:
        return ''
    }
  })

  /**
   * `styles.fill` иногда приходит 8-значным hex с альфа-каналом в хвосте (`#RRGGBBAA`,
   * например `#1976D22E`). Разбираем на непрозрачный цвет + числовую прозрачность —
   * иначе альфа из цвета и фиксированный `fillOpacity` в рендерере перемножаются и
   * заливка становится почти невидимой (а Yandex вообще не читает альфу из строки цвета,
   * ей нужно отдельное числовое поле `fillOpacity`).
   * @param fill
   */
  const parseFillColor = (fill: string): { color: string; opacity: number } => {
    if (fill?.length === 9 && fill.startsWith('#')) {
      const alpha = parseInt(fill.slice(7, 9), 16)
      if (Number.isFinite(alpha)) {
        return { color: fill.slice(0, 7), opacity: alpha / 255 }
      }
    }
    return { color: fill, opacity: 0.35 }
  }

  /**
   * Функция получения стиля объектов
   * @param object
   */
  const getFeatureStyle = (object: InventObject) => {
    const selected = isSelected(object)
    const { color: fillColor, opacity: fillOpacity } = parseFillColor(object.getStyles().fill)

    return {
      ...object.getStyles(),
      stroke: [
        {
          ...object.getStyles().stroke,
          color: selectedObjects.value.length
            ? selected
              ? 'var(--primary)'
              : 'var(--wight-grey-opacity)'
            : object.getStyles().stroke.color,
        },
      ],
      fill: selectedObjects.value.length
        ? selected
          ? 'var(--primary)'
          : 'var(--wight-grey-opacity)'
        : fillColor,
      fillOpacity: selectedObjects.value.length ? 0.35 : fillOpacity,
      zIndex: object.getId(),
      ...cursorStyleFromObject.value,
    }
  }

  /* Вспомогательные функции выделения */
  const mapSelect = useMapSelect()
  const { startSelectHandler, endSelectHandler } = mapSelect
  const areaSelect = useMapAreaSelect()

  /**
   * Функция обработки нажатия клавиш
   * @param e
   */
  const keyDownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (selectCoordsType.value) {
        areaSelect.cancelAreaSelect()
        return
      }
      if (isPlaceMode.value) {
        exitPlaceMode()
        return
      }
      initDrawHandler()
    }

    if (e.code === 'KeyZ' && e.ctrlKey) {
      cancelPointHandler()
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', keyDownHandler)
    document.addEventListener('keydown', modifierKeyDownHandler)
    document.addEventListener('keyup', modifierKeyUpHandler)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', keyDownHandler)
    document.removeEventListener('keydown', modifierKeyDownHandler)
    document.removeEventListener('keyup', modifierKeyUpHandler)
  })

  /**
   * Обработчик отпускания клавиши мыши.
   * Завершает выделение области или передает событие в текущий режим работы карты.
   */
  const mouseUpHandler = async (_: any, event: any) => {
    if (areaSelect.isDrawing) {
      areaSelect.endAreaSelect(_, event, selectCoordsType.value as 'square' | 'circle')
      if (selectCoordsType.value) {
        setMapDragBehaviorEnabled(isAreaSelectMapDragEnabled.value)
      }
      return
    }
    if (isPlaceMode.value) {
      if (!isQuickMapClick()) return
      await handlePlaceMapClick(event)
      return
    }
    await drawHandler(_, event)
    endSelectHandler(_, event, map)
  }

  /**
   * Обработчик нажатия клавиши мыши.
   * В режиме выбора области без Alt рисует область, а с Alt оставляет карте drag.
   */
  const mouseDownHandler = (_: any, event: any) => {
    if (selectCoordsType.value) {
      if (event.details?.altKey) return

      event?.originalEvent?.preventDefault?.()
      event?.domEvent?.preventDefault?.()
      event?.sourceEvent?.preventDefault?.()
      event?.preventDefault?.()
      areaSelect.startAreaSelect(_, event)
      return
    }
    drawDownHandler()
    if (isPlaceMode.value) return
    startSelectHandler(_, event, map)
  }

  /**
   * Центрирует карту по первому id из предвыбора, как только он появится среди
   * загруженных объектов (сервер вернёт его индивидуально благодаря `exclude_ids`).
   */
  const focusOnPreselected = (targetId: number) => {
    const target = objectsList.value.find((obj) => obj.getId() === targetId)
    const coords = (target?.getMapCoords() || []) as number[]
    if (coords.length === 2 && Number.isFinite(coords[0]) && Number.isFinite(coords[1])) {
      mapCenteredHandler(coords)
    }
  }

  watch(
    () => route.query.ids,
    (newIds) => {
      const idsString = String(newIds || '')
      const focus = String(route.query.focus || '') === '1'
      preSelectedObjectIds.value = idsString

      if (!idsString) {
        selectedObjects.value = []
        return
      }

      const ids = idsString.split(',').map(Number).filter(Boolean)
      /** Ставим выделение сразу, ещё до первой загрузки — так id уходит в `exclude_ids`
       *  первого же запроса и сервер вернёт объект индивидуально, а не в кластере. */
      selectedObjects.value = ids
      if (!focus || !ids.length) return

      if (objectsList.value.some((obj) => obj.getId() === ids[0])) {
        focusOnPreselected(ids[0])
      } else {
        const stop = watch(objectsList, () => {
          if (objectsList.value.some((obj) => obj.getId() === ids[0])) {
            focusOnPreselected(ids[0])
            stop()
          }
        })
      }
    },
    { immediate: true },
  )

  /**
   * Фиксирует исходные координаты вершин полигона/линии перед началом редактирования.
   * Если объект уже зарегистрирован в очереди ожидания — originalCoords не перезаписываются,
   * чтобы при повторных перемещениях откат происходил к самому первому состоянию геометрии.
   */
  const dragStartEditHandler = () => {
    if (!editSelectObject.value) return

    const obj = editSelectObject.value

    if (!moveStore.has(obj.getId())) {
      moveStore.register(obj.getId(), obj.getName(), JSON.parse(JSON.stringify(obj.getMapCoords())))
    }
  }

  const dragMoveEditHandler = (index: number, coordinates: any) => {
    if (!editSelectObject.value) return

    editSelectObject.value.setTransformPointCoords(index, coordinates)

    // Синхронизируем форму ручного ввода при перетаскивании вершин полигона/линии
    const obj = editSelectObject.value
    const mapCoords = obj.getMapCoords()
    coordsSync.value = {
      objectId: obj.getId(),
      coords: obj.isPolygon() ? (mapCoords as any[][])[0] : (mapCoords as any[]),
    }
  }

  /**
   * Функция удаления точки полигона
   * @param index
   * @param event
   */
  const clickEditHandler = (index: number, event: any) => {
    if (!editSelectObject.value) return

    if (event.ctrlKey || event.metaKey) {
      editSelectObject.value.deletePoint(index)
    }

    dragEndEditHandler()
  }

  /**
   * Регистрирует функции подтверждения и отката после изменения вершины полигона/линии
   * или добавления новой точки. Не блокирует взаимодействие с картой —
   * пользователь может продолжать редактировать, панель ожидает отдельного решения.
   */
  const dragEndEditHandler = () => {
    if (!editSelectObject.value) return

    const obj = editSelectObject.value
    const originalCoords = moveStore.entries.get(obj.getId())?.originalCoords

    moveStore.updateActions(
      obj.getId(),
      () => {
        if (!editSelectObject.value) return

        relocate(obj.getId(), {
          type: obj.isPolygon() ? 'Polygon' : 'LineString',
          coordinates: obj.getMapCoords(),
        } as InventoryMapGeometry)
      },
      () => {
        if (!editSelectObject.value || !originalCoords) return

        obj.getFeature().geometry = {
          ...obj.getFeature().geometry,
          coordinates: originalCoords,
        }

        coordsSync.value = {
          objectId: obj.getId(),
          coords: obj.isPolygon() ? (originalCoords as any[][])[0] : (originalCoords as any[]),
        }
      },
    )
  }

  const { isPending: isLoadCenter } = loadCenterMap()

  /**
   * Функция отслеживания перемещения мыши при трансформировании объекта
   * @param event
   */
  const objectMouseMoveHandler = (event: any) => {
    if (!event.details?.shiftKey || !editSelectObject.value) return

    let line = editSelectObject.value.getFeature() as any

    if (editSelectObject.value.isPolygon()) {
      line = polygonToLine(editSelectObject.value.getFeature() as any)
    }

    const mousePoint = point(event.coordinates)
    editNewPoint.value = nearestPointOnLine(line, mousePoint)
  }

  /**
   * Функция добавления новой точки при клике
   */
  const clickAddPointHandler = () => {
    if (!editSelectObject.value || !editNewPoint.value) return

    const segmentIndex = editNewPoint.value.properties.index
    const lineCoords = [...editSelectObject.value.getFeatureCoords()]

    lineCoords.splice(segmentIndex + 1, 0, editNewPoint.value.geometry.coordinates)

    editSelectObject.value.setTransformPointsCoords(lineCoords)

    dragEndEditHandler()
  }

  /**
   * Функция обработки центрирования карты по координатам объекта
   * @param coords
   */
  const mapCenteredHandler = (coords: number[]) => {
    center.value = coords
    zoom.value = 21
  }

  const mapObjectFocusedHandler = (coordinates: number[][]) => {
    const location = focusLocationFromCoordinates(coordinates)
    if (!location) return

    center.value = location.center
    zoom.value = location.zoom
  }

  /**
   * Функция раскрытия по клику на кластер/стопку.
   * Стопка (`colocated`) — раскрывается через `expand` (сервер сам считает кольцо и
   * присылает `members` при следующей загрузке), клик по уже раскрытой — сворачивает.
   * Обычный кластер — `object_ids` доступен при `count <= 500` и на максимальном zoom,
   * иначе просим приблизить карту (дальнейший zoom сам раздробит кластер на подкластеры).
   * Reload после изменения `selectedObjects`/`expandedClusterIds` триггерит общий
   * watcher в `model.ts` — здесь его вызывать не нужно.
   */
  const clusterSelectHandler = (cluster: InventoryMapCluster) => {
    if (cluster.colocated) {
      if (!expandedClusterIds.value.includes(cluster.id)) {
        expandedClusterIds.value = [...expandedClusterIds.value, cluster.id]
      }
      return
    }

    if (!cluster.object_ids) {
      addWarning(trs('itf.map_cluster_too_large'))
      return
    }

    selectedObjects.value = cluster.object_ids
  }

  /** Сворачивает раскрытую стопку (клик по «ножке»/центру кольца). */
  const collapseStackHandler = (clusterId: string) => {
    expandedClusterIds.value = expandedClusterIds.value.filter((id) => id !== clusterId)
  }

  /**
   * Поиск по выделенным координатам на карте
   */
  const selectCoordsType = ref<SelectCoordsType>('')
  const isAltPressed = ref(false)
  const searchModel = useGlobalSearch()
  const isAreaSelectMapDragEnabled = computed(
    () => !!selectCoordsType.value && isAltPressed.value && !areaSelect.isDrawing,
  )

  /**
   * Переключает поведение drag у текущего map-provider.
   * В area-select drag выключен по умолчанию и временно включается только при Alt.
   */
  const setMapDragBehaviorEnabled = (enabled: boolean) => {
    const entity = map.value?.entity
    if (!entity) return

    const hasDrag = entity.behaviors.includes('drag')

    if (enabled && !hasDrag) {
      entity.setBehaviors([...entity.behaviors, 'drag'])
    }

    if (!enabled && hasDrag) {
      entity.setBehaviors(entity.behaviors.filter((item: string) => item !== 'drag'))
    }
  }

  watch(selectCoordsType, (val) => {
    mapSelect.isDisabled = !!val
    if (val) {
      setMapDragBehaviorEnabled(isAreaSelectMapDragEnabled.value)
      return
    }

    areaSelect.cancelAreaSelect()
    setMapDragBehaviorEnabled(true)
  })

  watch(isAreaSelectMapDragEnabled, (enabled) => {
    if (!selectCoordsType.value) return

    setMapDragBehaviorEnabled(enabled)
  })

  /**
   * Функция задания выделенных координат
   * @param value
   */
  const areaSearchSelected = (value: any) => {
    searchModel.query.area = value
    searchModel.open = true
  }

  /**
   * Обработчик нажатия модификаторов.
   * Alt временно возвращает drag карты в area-select, Shift включает добавление точки в редактировании.
   */
  const modifierKeyDownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Alt') {
      isAltPressed.value = true
    }
    if (e.key === 'Shift') {
      setIsEditNewPoint(true)
    }
  }

  /**
   * Обработчик отпускания модификаторов.
   * Возвращает area-select в режим выделения области без перемещения карты и выключает добавление точки.
   */
  const modifierKeyUpHandler = (e: KeyboardEvent) => {
    if (e.key === 'Alt') {
      isAltPressed.value = false
    }
    if (e.key === 'Shift') {
      setIsEditNewPoint(false)
    }
  }

  return {
    action,
    addObjectType,
    areaSearchSelected,
    canRelocate,
    objectMoveBlockedHandler,
    center,
    clickAddPointHandler,
    clickEditHandler,
    clickHandler,
    clusterSelectHandler,
    collapseStackHandler,
    dragEndEditHandler,
    dragMoveEditHandler,
    dragStartEditHandler,
    editFeature,
    editNewPoint,
    editSelectObject,
    filter,
    full,
    getFeatureStyle,
    inventObjectTypes,
    typesDict,
    isAreaSelectMapDragEnabled,
    isEditNewPoint,
    isLoadCenter,
    isMapBusy,
    isPlaceMode,
    mapCenteredHandler,
    mapObjectFocusedHandler,
    mapType,
    useAlternativeMap,
    mouseDownHandler,
    mouseMoveHandler,
    mouseUpHandler,
    objectClickHandler,
    objectEndDragHandler,
    objectMoveDragHandler,
    objectStartDragHandler,
    objects,
    objectsList,
    placePointerType,
    pointerCount,
    pointerScreenCoordinates,
    selectCoordsType,
    selectedHandler,
    selectedObjects,
    skipInitialBoundsFit,
    updateCoordsHandler,
    updateHandler,
    zoom,
    zoomSmallMarker,
    zoomSwitchValue,
  }
}
